import {
  type ExternalText,
  ExternalTextSchema,
} from "contracts/boundaries/external-text";
import type { MapPage, MapVocabulary } from "../mapTypes";

type EstateArchetype = Exclude<MapVocabulary["kind"], "general">;

/**
 * Guessing what KIND of site an estate is, from the words it uses in its own
 * URLs and page titles.
 *
 * There is no field that says "this is a retailer" — the vocabulary is the
 * only signal, and it is the crawled site's vocabulary, not Faro's. The word
 * lists and the scoring live here; the map receives a scored archetype and
 * never re-reads a path or a title itself.
 */

const PAGE_PATTERNS: Record<EstateArchetype, RegExp> = {
  commerce:
    /(?:^|[^a-z0-9])(?:accessor(?:y|ies)|buy|campaign|cart|checkout|collection|comparison|discover|device|order|product|purchase|shop|store)(?:$|[^a-z0-9])/i,
  editorial:
    /(?:^|[^a-z0-9])(?:article|blog|breaking|coverage|editorial|latest[-\s]?news|news|newsletter|opinion|sport|story|subscribe|subscription|topic|world)(?:s)?(?:$|[^a-z0-9])/i,
  service:
    /(?:^|[^a-z0-9])(?:apply|application|appointment|book|care|contact|enquir(?:y|ies)|inquir(?:y|ies)|quote|service|support)(?:s)?(?:$|[^a-z0-9])/i,
};

const STRONG_PATTERNS: Record<EstateArchetype, RegExp> = {
  commerce:
    /(?:^|[^a-z0-9])(?:buy|cart|checkout|order|purchase)(?:s)?(?:$|[^a-z0-9])/i,
  editorial:
    /(?:^|[^a-z0-9])(?:newsletter|subscribe|subscription)(?:s)?(?:$|[^a-z0-9])/i,
  service:
    /(?:^|[^a-z0-9])(?:apply|application|appointment|book|contact|enquir(?:y|ies)|inquir(?:y|ies)|quote)(?:s)?(?:$|[^a-z0-9])/i,
};

const IDENTITY_PATTERNS: Record<EstateArchetype, RegExp> = {
  commerce: /(?:^|[^a-z0-9])(?:marketplace|retail|shop|store)(?:$|[^a-z0-9])/i,
  editorial:
    /(?:^|[^a-z0-9])(?:gazette|herald|journal|newspaper|publisher|times)(?:$|[^a-z0-9])/i,
  service:
    /(?:^|[^a-z0-9])(?:agency|clinic|consultancy|services)(?:$|[^a-z0-9])/i,
};

/** Whether the site's own name reads like this archetype. */
export function identityMatches(
  kind: EstateArchetype,
  identity: ExternalText,
): boolean {
  return IDENTITY_PATTERNS[kind].test(identity);
}

/** Whether this page's URL or title uses the archetype's vocabulary at all. */
export function pageMatches(kind: EstateArchetype, page: MapPage): boolean {
  return PAGE_PATTERNS[kind].test(pageText(page));
}

function pageText(page: MapPage): ExternalText {
  return ExternalTextSchema.parse(`${page.path} ${page.title ?? ""}`);
}

/** Whether it uses the archetype's most decisive words. */
export function pageStronglyMatches(
  kind: EstateArchetype,
  page: MapPage,
): boolean {
  return STRONG_PATTERNS[kind].test(pageText(page));
}
